import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, ArrowLeft, Building2, Eye } from 'lucide-react';
import Sidebar from '../../components/Sidebar';
import Header from '../../components/Header';
import apiService from '../../utils/api';
import { useToast } from '../../components/Toast';
import { sortedByName } from '../../utils/sortAlpha';

const formatCurrency = (v) => `₹${parseFloat(v || 0).toFixed(2)}`;
const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-IN') : '-';

function DairyFarmPage() {
  const toast = useToast();
  const [farms, setFarms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [currentView, setCurrentView] = useState('list');
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({ name: '', address: '', phones: [''], fssai_number: '', description: '', invoice_prefix: '' });

  // overview
  const [overview, setOverview] = useState(null);
  const [overviewLoading, setOverviewLoading] = useState(false);
  const [overviewFilters, setOverviewFilters] = useState({ start_date: '', end_date: '', customer_id: '' });
  const [customers, setCustomers] = useState([]);

  const load = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await apiService.getDairyFarms();
      setFarms(res.data || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    apiService.getCustomers().then(res => setCustomers(res.data || [])).catch(() => {});
  }, []);

  const emptyForm = () => ({ name: '', address: '', phones: [''], fssai_number: '', description: '', invoice_prefix: '' });

  const handleAdd = () => {
    setEditingItem(null);
    setFormData(emptyForm());
    setCurrentView('form');
  };

  const handleViewOverview = async (farm) => {
    setEditingItem(farm);
    setCurrentView('overview');
    setOverview(null);
    setOverviewFilters({ start_date: '', end_date: '', customer_id: '' });
    await loadOverview(farm, { start_date: '', end_date: '', customer_id: '' });
  };

  const loadOverview = async (farm, overrideFilters) => {
    try {
      setOverviewLoading(true);
      const res = await apiService.getDairyFarmOverview(farm.farm_id, overrideFilters ?? overviewFilters);
      setOverview(res.data);
    } catch (err) {
      toast.error('Failed to load overview: ' + err.message);
    } finally {
      setOverviewLoading(false);
    }
  };

  const handleEdit = (farm) => {
    setEditingItem(farm);
    const phones = (farm.phone || '').split(',').map(p => p.trim()).filter(Boolean);
    setFormData({
      name: farm.name,
      address: farm.address || '',
      phones: phones.length > 0 ? phones : [''],
      fssai_number: farm.fssai_number || '',
      description: farm.description || '',
      invoice_prefix: farm.invoice_prefix || '',
    });
    setCurrentView('form');
  };

  const handleCancel = () => {
    setCurrentView('list');
    setEditingItem(null);
    setFormData(emptyForm());
    setError(null);
  };

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handlePhoneChange = (index, value) => {
    setFormData(prev => ({ ...prev, phones: prev.phones.map((p, i) => i === index ? value : p) }));
  };

  const addPhoneField = () => {
    setFormData(prev => ({ ...prev, phones: [...prev.phones, ''] }));
  };

  const removePhoneField = (index) => {
    setFormData(prev => ({ ...prev, phones: prev.phones.filter((_, i) => i !== index) }));
  };

  const handleSubmit = async () => {
    if (!formData.name.trim()) {
      toast.warning('Farm name is required');
      return;
    }
    try {
      setLoading(true);
      const payload = {
        name: formData.name,
        address: formData.address,
        phone: formData.phones.map(p => p.trim()).filter(Boolean).join(', '),
        fssai_number: formData.fssai_number,
        description: formData.description,
        invoice_prefix: formData.invoice_prefix,
      };
      if (editingItem) {
        await apiService.updateDairyFarm(editingItem.farm_id, payload);
      } else {
        await apiService.createDairyFarm(payload);
      }
      await load();
      setCurrentView('list');
      setEditingItem(null);
    } catch (err) {
      toast.error('Error saving dairy farm: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (farm) => {
    if (!window.confirm(`Delete "${farm.name}"?`)) return;
    try {
      setLoading(true);
      await apiService.deleteDairyFarm(farm.farm_id);
      await load();
    } catch (err) {
      toast.error('Error deleting dairy farm: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const renderLayout = (content) => (
    <div className="app-container">
      <Header />
      <div className="main-content">
        <Sidebar />
        <div className="page-content">{content}</div>
      </div>
    </div>
  );

  if (currentView === 'overview') {
    return renderLayout(
      <>
        <div className="page-header">
          <div className="header-left">
            <button className="btn btn-secondary" onClick={() => { setCurrentView('list'); setEditingItem(null); setOverview(null); }} style={{ marginRight: '1rem' }}>
              <ArrowLeft size={18} /> Back
            </button>
            <h1 className="page-title" style={{ marginTop: '1rem' }}>{editingItem?.name} — Overview</h1>
          </div>
        </div>

        <div className="filter-bar">
          <div className="filter-group">
            <label className="form-label" style={{ marginBottom: 0, fontSize: '0.8rem' }}>Customer</label>
            <select
              className="form-select"
              style={{ minWidth: '160px' }}
              value={overviewFilters.customer_id}
              onChange={e => {
                const next = { ...overviewFilters, customer_id: e.target.value };
                setOverviewFilters(next);
                loadOverview(editingItem, next);
              }}
            >
              <option value="">All Customers</option>
              {sortedByName(customers).map(c => (
                <option key={c.customer_id} value={c.customer_id}>{c.name}</option>
              ))}
            </select>
          </div>
          <div className="filter-group">
            <label className="form-label" style={{ marginBottom: 0, fontSize: '0.8rem' }}>Start Date</label>
            <input
              type="date"
              className="form-input"
              value={overviewFilters.start_date}
              onChange={e => {
                const next = { ...overviewFilters, start_date: e.target.value };
                setOverviewFilters(next);
                loadOverview(editingItem, next);
              }}
            />
          </div>
          <div className="filter-group">
            <label className="form-label" style={{ marginBottom: 0, fontSize: '0.8rem' }}>End Date</label>
            <input
              type="date"
              className="form-input"
              value={overviewFilters.end_date}
              onChange={e => {
                const next = { ...overviewFilters, end_date: e.target.value };
                setOverviewFilters(next);
                loadOverview(editingItem, next);
              }}
            />
          </div>
          <button
            className="btn btn-secondary"
            style={{ height: '42px', alignSelf: 'flex-end' }}
            onClick={() => {
              const next = { start_date: '', end_date: '', customer_id: '' };
              setOverviewFilters(next);
              loadOverview(editingItem, next);
            }}
          >
            Clear
          </button>
        </div>

        {overviewLoading && <div className="loading">Loading overview...</div>}

        {!overviewLoading && overview && (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
              {[
                ['Bills (invoiced)', overview.totals.bill_count],
                ['Total Sales', formatCurrency(overview.totals.total_sales)],
                ['Invoices', overview.totals.invoice_count],
                ['Total Invoiced', formatCurrency(overview.totals.total_invoiced)],
                ['Total Paid', formatCurrency(overview.totals.total_paid)],
                ['Total Pending', formatCurrency(overview.totals.total_pending)],
              ].map(([label, value]) => (
                <div key={label} className="card" style={{ padding: '1rem' }}>
                  <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>{label}</div>
                  <div style={{ fontWeight: 700, fontSize: '1.1rem' }}>{value}</div>
                </div>
              ))}
            </div>

            <div className="card" style={{ marginBottom: '1.5rem' }}>
              <div className="card-header"><h3 className="card-title">Invoices</h3></div>
              <div className="table-container" style={{ margin: 0, boxShadow: 'none', border: 'none', maxHeight: '350px', overflowY: 'auto' }}>
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Invoice No</th>
                      <th>Date</th>
                      <th>Customer</th>
                      <th>Amount</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {overview.invoices.map(inv => (
                      <tr key={inv.invoice_id}>
                        <td style={{ fontWeight: 600 }}>{inv.invoice_number}</td>
                        <td>{formatDate(inv.invoice_date)}</td>
                        <td>{inv.customer_name}</td>
                        <td style={{ fontWeight: 600 }}>{formatCurrency(inv.total_amount)}</td>
                        <td><span className={`status-badge ${inv.status === 'paid' ? 'available' : 'unavailable'}`}>{inv.status}</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {overview.invoices.length === 0 && <div className="no-data"><p>No invoices for this farm yet</p></div>}
              </div>
            </div>

            <div className="card">
              <div className="card-header"><h3 className="card-title">Customers</h3></div>
              <div className="table-container" style={{ margin: 0, boxShadow: 'none', border: 'none', maxHeight: '350px', overflowY: 'auto' }}>
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Customer</th>
                      <th>Total Billed</th>
                      <th>Total Paid</th>
                      <th>Balance</th>
                    </tr>
                  </thead>
                  <tbody>
                    {overview.customers.map(c => (
                      <tr key={c.customer_id}>
                        <td style={{ fontWeight: 500 }}>{c.name}</td>
                        <td>{formatCurrency(c.total_billed)}</td>
                        <td>{formatCurrency(c.total_paid)}</td>
                        <td style={{ fontWeight: 600, color: c.balance > 0 ? 'var(--error-color)' : 'var(--success-color)' }}>{formatCurrency(c.balance)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {overview.customers.length === 0 && <div className="no-data"><p>No customers under this farm yet</p></div>}
              </div>
            </div>
          </>
        )}
      </>
    );
  }

  if (currentView === 'form') {
    return renderLayout(
      <>
        <div className="page-header">
          <div className="header-left">
            <button className="btn btn-secondary" onClick={handleCancel}>
              <ArrowLeft size={18} /> Back
            </button>
            <h1 className="page-title" style={{ marginTop: '1rem' }}>
              {editingItem ? 'Edit Dairy Farm' : 'Add Dairy Farm'}
            </h1>
          </div>
        </div>

        <div className="card">
          <div className="form-group">
            <label className="form-label">Farm Name <span style={{ color: 'var(--error-color)' }}>*</span></label>
            <input
              type="text"
              name="name"
              className="form-input"
              value={formData.name}
              onChange={handleInputChange}
              placeholder="e.g. Hirap Dairy Farm"
              disabled={loading}
            />
          </div>
          <div className="form-group">
            <label className="form-label">Address</label>
            <textarea
              name="address"
              className="form-input"
              value={formData.address}
              onChange={handleInputChange}
              placeholder="Farm address"
              rows={3}
              disabled={loading}
            />
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Description <span style={{ color: 'var(--text-secondary)', fontWeight: 400 }}>(optional, shown below the farm name on bills)</span></label>
              <input
                type="text"
                name="description"
                className="form-input"
                value={formData.description}
                onChange={handleInputChange}
                placeholder="e.g. Wholesale supplier for cow and buffalo milk"
                disabled={loading}
              />
            </div>
            <div className="form-group">
              <label className="form-label">FSSAI Number <span style={{ color: 'var(--text-secondary)', fontWeight: 400 }}>(optional)</span></label>
              <input
                type="text"
                name="fssai_number"
                className="form-input"
                value={formData.fssai_number}
                onChange={handleInputChange}
                placeholder="e.g. 12345678901234"
                disabled={loading}
              />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Invoice Initial </label>
              <input
                type="text"
                name="invoice_prefix"
                className="form-input"
                value={formData.invoice_prefix}
                onChange={e => setFormData(prev => ({ ...prev, invoice_prefix: e.target.value.toUpperCase() }))}
                placeholder="e.g. HP"
                maxLength={10}
                disabled={loading}
              />
            </div>
            <div className="form-group">
              <label className="form-label">Phone Number(s)</label>
              {formData.phones.map((phone, i) => (
                <div key={i} style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem' }}>
                  <input
                    type="text"
                    className="form-input"
                    value={phone}
                    onChange={e => handlePhoneChange(i, e.target.value)}
                    placeholder="e.g. +91 98765 43210"
                    disabled={loading}
                  />
                  {formData.phones.length > 1 && (
                    <button type="button" className="btn btn-secondary" style={{ padding: '4px 10px', color: 'var(--error)' }} onClick={() => removePhoneField(i)} disabled={loading}>
                      <Trash2 size={14} />
                    </button>
                  )}
                </div>
              ))}
              <button type="button" className="btn btn-secondary" style={{ padding: '4px 10px' }} onClick={addPhoneField} disabled={loading}>
                <Plus size={14} /> Add Phone Number
              </button>
            </div>
          </div>
          <div className="form-actions" style={{ display: 'flex', gap: '0.75rem', marginTop: '1.5rem' }}>
            <button className="btn btn-primary" onClick={handleSubmit} disabled={loading}>
              {loading ? 'Saving...' : editingItem ? 'Update Farm' : 'Add Farm'}
            </button>
            <button className="btn btn-secondary" onClick={handleCancel} disabled={loading}>
              Cancel
            </button>
          </div>
        </div>
      </>
    );
  }

  return renderLayout(
    <>
      <div className="page-header">
        <div className="header-left">
          <h1 className="page-title">
            <Building2 size={24} style={{ marginRight: '0.5rem' }} />
            Dairy Farms
          </h1>
        </div>
        <button className="btn btn-primary" onClick={handleAdd}>
          <Plus size={18} /> Add Farm
        </button>
      </div>

      {error && <div className="error-container"><p>{error}</p></div>}

      {loading && farms.length === 0 ? (
        <div className="loading">Loading dairy farms...</div>
      ) : farms.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', padding: '3rem', color: 'var(--text-secondary)' }}>
          <Building2 size={48} style={{ marginBottom: '1rem', opacity: 0.4 }} />
          <p>No dairy farms added yet.</p>
          <button className="btn btn-primary" style={{ marginTop: '1rem' }} onClick={handleAdd}>
            <Plus size={18} /> Add Your First Farm
          </button>
        </div>
      ) : (
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Farm Name</th>
                <th>Address</th>
                <th>Phone(s)</th>
                <th>FSSAI No</th>
                <th>Invoice Initial</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {farms.map((farm) => (
                <tr key={farm.farm_id}>
                  <td style={{ color: 'var(--text-secondary)', fontSize: '0.85rem' }}>{farm.farm_id}</td>
                  <td style={{ fontWeight: 600 }}>
                    {farm.name}
                    {farm.description && <div style={{ fontSize: '0.78rem', fontWeight: 400, color: 'var(--text-secondary)' }}>{farm.description}</div>}
                  </td>
                  <td style={{ color: 'var(--text-secondary)' }}>{farm.address || '—'}</td>
                  <td style={{ color: 'var(--text-secondary)' }}>{farm.phone || '—'}</td>
                  <td style={{ color: 'var(--text-secondary)' }}>{farm.fssai_number || '—'}</td>
                  <td style={{ color: 'var(--text-secondary)' }}>{farm.invoice_prefix || '—'}</td>
                  <td>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <button
                        className="btn btn-secondary"
                        style={{ padding: '4px 10px' }}
                        onClick={() => handleViewOverview(farm)}
                        disabled={loading}
                        title="View Overview"
                      >
                        <Eye size={14} />
                      </button>
                      <button
                        className="btn btn-secondary"
                        style={{ padding: '4px 10px' }}
                        onClick={() => handleEdit(farm)}
                        disabled={loading}
                      >
                        <Edit size={14} />
                      </button>
                      <button
                        className="btn btn-secondary"
                        style={{ padding: '4px 10px', color: 'var(--error-color)' }}
                        onClick={() => handleDelete(farm)}
                        disabled={loading}
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

export default DairyFarmPage;
